import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';
import { steps } from '../data/howToSteps';


const HowToPlay = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Play My Football Game',
    description: 'A step-by-step beginner guide to My Football Game: create your club, set tactics, challenge managers, trade players and climb the global leaderboard.',
    image: 'https://www.myfootballgame.com/logo.png',
    totalTime: 'PT5M',
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.title,
      text: s.text
    }))
  };

  return (
    <motion.div
      className="w-full min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Seo
        title="How to Play - Beginner's Guide | My Football Game"
        description="Learn how to play My Football Game in 8 simple steps: create your club, set tactics, sign players on the transfer market, challenge friends and win matches."
        path="/how-to-play"
        jsonLd={jsonLd}
      />
      <div className="container mx-auto px-4">
        <Header />
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-10"
        >
          <div className="pc-eyebrow pc-eyebrow-accent mb-3">Beginner's Guide</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">How to Play My Football Game</h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            From your first login to lifting trophies - everything a new manager needs to know,
            in eight steps. It takes less than five minutes to get started.
          </p>
        </motion.div>

        <div className="space-y-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.06 }}
              className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-lg p-5 shadow-md flex gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-green-900/40 border border-green-500/30 flex items-center justify-center flex-shrink-0 text-green-400 font-bold">
                {index + 1}
              </div>
              <div>
                <h2 className="text-lg font-semibold mb-1">{step.title}</h2>
                <p className="text-white/80">{step.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-10 bg-gradient-to-r from-green-900/30 to-emerald-900/30 p-6 rounded-xl border border-green-500/20 text-center"
        >
          <h3 className="text-xl font-bold mb-3">Ready for your first match?</h3>
          <p className="text-white/80 mb-6">
            Create your club for free and challenge your first opponent today. Have questions?
            Check the <Link to="/faq" className="text-green-400 hover:underline">FAQ</Link> or
            dive deeper with our <Link to="/blog" className="text-green-400 hover:underline">manager guides</Link>.
          </p>
          <Link
            to="/register"
            className="inline-block px-6 py-2.5 bg-[var(--accent)] rounded-lg font-medium transition-colors shadow-md"
          >
            Create Your Club Free
          </Link>
        </motion.div>
      </div>

      <Footer />
    </motion.div>
  );
};

export default HowToPlay;
